import { useCallback, useEffect, useRef } from 'react';
import type { StartPromiseReturn } from '@openreplay/tracker/lib/app';
import { useSelector } from 'react-redux';
import { State } from './store';
import OpenReplayTracker from '@openreplay/tracker';

const isDev = window.location.origin.includes('flowdev');

const getProjectCredentials = () =>
  isDev || LOCAL
    ? {
        projectKey: 'tuuwLFbwpqyG9ATZgedP',
        ingestPoint: 'https://openreplay.internal.telnyx.com/ingest',
      }
    : {
        projectKey: 'c982r1iyIbbrYmUvALj9',
        ingestPoint: 'https://openreplay.telnyx.com/ingest',
      };

declare global {
  // https://github.com/typescript-eslint/typescript-eslint/issues/3617
  // eslint-disable-next-line no-var
  var OpenReplay: StartPromiseReturn;
}

// https://docs.openreplay.com/en/sdk/constructor/#example
export const OpenReplay = () => {
  const trackerRef = useRef<OpenReplayTracker | null>(null);

  const username = useSelector((state: State) => state.auth.username);
  const userEmail = useSelector((state: State) => state.auth.email);

  const load = useCallback(async () => {
    const { projectKey, ingestPoint } = getProjectCredentials();

    try {
      const tracker = new OpenReplayTracker({
        projectKey,
        ingestPoint,
        defaultInputMode: 0,
        obscureTextEmails: false,
        obscureTextNumbers: false,
        obscureInputEmails: false,
        obscureInputNumbers: false,
        obscureInputDates: false,
        __debug__: isDev ? 5 : 0, // 5 is the highest level of debug, 0 is silent
        network: {
          capturePayload: true,
          failuresOnly: true,
          sessionTokenHeader: false,
          ignoreHeaders: true,
          captureInIframes: true,
        },
      });

      trackerRef.current = tracker;

      const openReplaySessionInfo = await tracker.start();

      global.OpenReplay = openReplaySessionInfo;
      return openReplaySessionInfo.success;
    } catch (e) {
      console.error('Open Replay failed to load', e);
    }
  }, []);

  useEffect(() => {
    // internal client does not use open replay
    if (INTERNAL_CLIENT) return;

    if (!username || !userEmail) {
      setTimeout(async () => {
        const openReplayLoaded = await load();
        if (openReplayLoaded) console.log('OpenReplay loaded');
      }, 300);
      return;
    }

    if (trackerRef.current) {
      const type = userEmail?.includes('@telnyx.com') ? 'internal' : 'external';

      trackerRef.current.setUserID(username);
      trackerRef.current.setMetadata('email', userEmail);
      trackerRef.current.setMetadata('userType', type);
    }
  }, [username, userEmail, load]);

  return null;
};

export default OpenReplay;
